if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.actionComplete = this.submitButton.querySelector('.action-complete');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this._addedTimer) {
          clearTimeout(this._addedTimer);
          this._addedTimer = null;
        }
        this.submitButton.classList.remove('added');
        this.actionComplete?.classList.add('hidden');

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');

        const formData = new FormData(this.form);
        const productId = formData.get('id');
        const cardId = formData.get('property[_card_id]');
        const cardType = formData.get('property[_card_type]');
        const cardMessage = formData.get('property[_card_message]');
        const properties = {};

        // Add all fields with property[key] to the properties object
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('property[')) {
            const propertyKey = key.match(/property\[(.*?)\]/)?.[1] || false;

            if (propertyKey) {
              properties[propertyKey] = value;
            }
          }
        }

        // Post-add card modal: optional flower/hamper line gets _bundle_id so the card can be added
        // later without merging; only when theme uses modal card selector (not `cardId`—non-card products
        // never have a card_id).
        const cardSelectorIsModal = this.dataset.cardSelectorType === 'modal';
        if (cardSelectorIsModal && !cardId && this.dataset.cardOptional === 'true' && this.dataset.cardProduct !== 'true') {
          properties['_bundle_id'] = Date.now().toString(36) + Math.random().toString(36).substring(2);
        }
        // if (cardSelectorIsModal && this.dataset.cardProduct === 'true') {
        //   properties['_card'] = true;
        //   properties['_type'] = this.dataset.cardType || 'special';
        // }

        const data = {
          items: [
            {
              id: productId,
              quantity: formData.get('quantity'),
              properties,
            },
          ],
        };

        // If the card type is special or complimentary (inline flow), add the card to the cart
        if (cardId) {
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          const cartData = await cartResponse.json();

          const cardAlreadyExists = cartData.items.some(
            (item) =>
              item.variant_id == productId &&
              item.properties &&
              item.properties._card_id == cardId &&
              item.properties._card_type == cardType &&
              item.properties._card_message == cardMessage
          );

          if (!cardAlreadyExists) {
            data.items.push({
              id: cardId,
              parent_id: productId,
              quantity: 1,
              properties: {
                _card: true,
                _type: cardType,
                Message: cardMessage,
              },
            });
          }
        }

        if (this.cart) {
          data.sections = this.cart.getSectionsToRender().map((section) => section.id);
          data.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(data);

        fetch(`${routes.cart_add_url}.js`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
                cardOptional: this.dataset.cardOptional === 'true',
                cardProduct: this.dataset.cardProduct === 'true',
                cardType: this.dataset.cardType || 'special',
                parentLineKey: response.items?.[0]?.key,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;

            // If inline card selector, render cart drawer directly
            if (!cardSelectorIsModal) {
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure('add:paint-updated-sections', () => {
                        this.cart.renderContents(response);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (!this.error) {
              this.submitButton.classList.add('added');
              this.actionComplete?.classList.remove('hidden');
              this._addedTimer = setTimeout(() => {
                this.submitButton.classList.remove('added');
                this.actionComplete?.classList.add('hidden');
              }, 3000);
            }

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
